/**
 * Axios instance.
 *
 * `withCredentials` is the whole story: the session travels as an HTTP-only
 * cookie, so requests must carry it and CORS must allow credentials.
 *
 * Gone from the old version: the Bearer-token request interceptor and the
 * 401 → refresh → retry response interceptor. There is no access token to attach
 * and none to refresh. A 401 now means the session has genuinely expired, so the
 * only sensible response is to send the user to sign in.
 */
import axios from 'axios';
import {
  TENANT_HINT_HEADER,
  TUTOR_REQUEST_TIMEOUT_MS,
  type ApiEnvelope,
} from '@cwy/config/contracts';
import { apiOrigin } from '@/lib/apiOrigin';
import { LOGIN_PATH, supportSchoolSlug, surfaceForPath, tenant, tenantPath } from '@/lib/tenant';

export const api = axios.create({
  baseURL: `${apiOrigin}/api/v1`,
  timeout: 20_000,
  withCredentials: true,
});

/**
 * Attach the workspace hint to every request.
 *
 * ONE interceptor, so no call site can forget it — and the browser and API are
 * on different origins, so the `/school/:slug` path the user is on does not
 * otherwise travel with the request.
 *
 * A hint, not authority: the API resolves it to a tenant and then checks the
 * caller's membership and each resource's tenant foreign key. Sending a
 * different slug gets you a 403, not another school's data.
 */
api.interceptors.request.use((config) => {
  if (config.url?.startsWith('/tutor/')) config.timeout = TUTOR_REQUEST_TIMEOUT_MS;
  // Platform support sends the managed school's slug to the school console's
  // routes and nowhere else: `/me`, the platform routes and the curriculum tree
  // keep answering for the operator's own context. The API admits it only for a
  // super admin, and records it in that school's activity log.
  const support = config.url?.startsWith('/tenant-admin') ? supportSchoolSlug() : null;
  const slug = tenant.slug ?? support;
  if (slug) {
    config.headers.set(TENANT_HINT_HEADER, slug);
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error.response?.status;
    // Never redirect away from the auth screens themselves, or a failed sign-in
    // attempt would bounce the user mid-typing.
    // Compared against the path WITHIN this workspace, so `/school/x/login` is
    // recognised as the sign-in screen just as `/login` is.
    const withinTenant =
      tenant.basename === '/'
        ? window.location.pathname
        : window.location.pathname.slice(tenant.basename.length) || '/';
    const onAuthScreen = ['/login', '/staff', '/signup', '/', '/verify-email'].includes(
      withinTenant,
    );
    if (status === 401 && !onAuthScreen) {
      // tenantPath keeps the workspace: a hard navigation bypasses the router, so
      // a bare path would silently drop a school user to the root domain. The
      // surface keeps an expired staff session on `/staff` rather than depositing
      // them at the student sign-in.
      window.location.assign(tenantPath(LOGIN_PATH[surfaceForPath(withinTenant)]));
    }
    return Promise.reject(error);
  },
);

/** Unwrap the `{ success, data, error }` envelope, throwing on failure. */
export function unwrap<T>(envelope: ApiEnvelope<T>): T {
  if (!envelope.success || envelope.data === undefined) {
    throw new Error(envelope.error ?? 'Request failed');
  }
  return envelope.data;
}

export async function apiGet<T>(url: string): Promise<T> {
  const { data } = await api.get<ApiEnvelope<T>>(url);
  return unwrap(data);
}

export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.post<ApiEnvelope<T>>(url, body);
  return unwrap(data);
}

export async function apiPut<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.put<ApiEnvelope<T>>(url, body);
  return unwrap(data);
}

export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.patch<ApiEnvelope<T>>(url, body);
  return unwrap(data);
}

export async function apiDelete<T>(url: string): Promise<T> {
  const { data } = await api.delete<ApiEnvelope<T>>(url);
  return unwrap(data);
}

/**
 * The sentence to show a person when a call fails.
 *
 * axios rejects a 4xx before `unwrap` sees the envelope, so its own message —
 * "Request failed with status code 422" — is what reaches the screen unless the
 * envelope's `error` is read off the response. The server writes those for
 * people; use them.
 */
export function apiErrorMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const envelope = (err as { response?: { data?: { error?: unknown } } }).response?.data;
    if (typeof envelope?.error === 'string' && envelope.error) return envelope.error;
    const message = (err as { message?: string }).message;
    if (message) return message;
  }
  return 'Something went wrong. Please try again.';
}

/**
 * Upload a file straight to S3 with a presigned PUT URL.
 *
 * Not an API call at all — that is the point. `baseURL` is cleared and the body
 * is passed through untransformed so axios does not JSON-encode the blob. Every
 * upload path in the app goes through here now, including the teacher's question
 * paper, which used to be multiparted through Express.
 */
export async function uploadToS3(
  uploadUrl: string,
  blob: Blob,
  contentType: string,
  onProgress?: (percent: number) => void,
): Promise<void> {
  await axios.put(uploadUrl, blob, {
    baseURL: '',
    withCredentials: false,
    headers: { 'Content-Type': contentType },
    transformRequest: [(data) => data],
    timeout: 120_000,
    onUploadProgress: onProgress
      ? (event) => {
          if (event.total) onProgress(Math.round((event.loaded / event.total) * 100));
        }
      : undefined,
  });
}
